import { useEffect, useRef, useState } from "react";
import {
  HardDrivesIcon as HardDrives,
  CameraIcon as Camera,
  ClockClockwiseIcon as ClockClockwise,
  CircleNotchIcon as Spinner,
} from "@phosphor-icons/react";
import { formatBytes, formatRelative } from "@/lib/metadataConstants";

export default function BackupsStrip({
  backups,
  snapshotting,
  onSnapshot,
  onRestore,
}) {
  // Compact info strip — same mono uppercase treatment as Pipeline Velocity.
  // Shows latest 3 snapshots with relative time + size, plus a "+N older" tail,
  // a Restore dropdown, and a one-click "Snapshot now" button. The restore
  // menu lives under a small native <button>+<div> menu (dependency-free).
  const recent = (backups || []).slice(0, 3);
  const more = Math.max(0, (backups?.length || 0) - recent.length);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const restoreRef = useRef(null);

  // Click-away to close the inline menu.
  useEffect(() => {
    if (!restoreOpen) return undefined;
    const onDocClick = (e) => {
      if (restoreRef.current && !restoreRef.current.contains(e.target)) {
        setRestoreOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [restoreOpen]);

  const handlePick = (name) => {
    setRestoreOpen(false);
    onRestore(name);
  };

  return (
    <div
      className="flex flex-wrap items-center gap-3 text-sm border-l-2 border-[var(--ml-primary)] bg-[var(--ml-surface)] px-3 py-2"
      data-testid="backups-strip"
    >
      <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5">
        <HardDrives size={11} weight="bold" /> Backups
      </span>
      {recent.length === 0 ? (
        <span
          className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]"
          data-testid="backups-empty"
        >
          No snapshots yet · the first one auto-runs ~60s after startup
        </span>
      ) : (
        recent.map((b) => (
          <span
            key={b.name}
            className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]"
            title={`${b.name} · ${formatBytes(b.size_bytes)} · ${new Date(b.mtime).toLocaleString()}`}
            data-testid={`backup-${b.name}`}
          >
            <strong className="text-[var(--ml-text-primary)] font-semibold mr-1">
              {formatRelative(b.mtime)}
            </strong>
            {formatBytes(b.size_bytes)}
          </span>
        ))
      )}
      {more > 0 && (
        <span
          className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]"
          data-testid="backups-more"
        >
          + {more} older
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        {backups && backups.length > 0 && (
          <div ref={restoreRef} className="relative">
            <button
              type="button"
              onClick={() => setRestoreOpen((s) => !s)}
              className="ml-btn-ghost text-[var(--ml-text-primary)] flex items-center gap-1.5"
              data-testid="restore-menu-btn"
            >
              <ClockClockwise size={12} weight="bold" /> Restore
            </button>
            {restoreOpen && (
              <div
                className="absolute right-0 top-full mt-1 z-20 min-w-[260px] bg-white border border-[var(--ml-border)] shadow-lg"
                data-testid="restore-menu"
              >
                <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] px-3 py-2 border-b border-[var(--ml-border)]">
                  Pick a snapshot to restore
                </div>
                {backups.map((b) => (
                  <button
                    key={b.name}
                    type="button"
                    onClick={() => handlePick(b.name)}
                    data-testid={`restore-option-${b.name}`}
                    className="w-full text-left px-3 py-2 hover:bg-[var(--ml-surface)] flex items-center justify-between gap-3 border-b last:border-b-0 border-[var(--ml-border)]"
                  >
                    <span className="text-sm font-mono">{b.name}</span>
                    <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                      {formatRelative(b.mtime)} · {formatBytes(b.size_bytes)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={onSnapshot}
          disabled={snapshotting}
          className="ml-btn-ghost text-[var(--ml-primary)] disabled:opacity-50 flex items-center gap-1.5"
          data-testid="snapshot-now-btn"
        >
          {snapshotting ? (
            <Spinner size={12} className="animate-spin" />
          ) : (
            <Camera size={12} weight="bold" />
          )}
          {snapshotting ? "Snapshotting…" : "Snapshot now"}
        </button>
      </div>
    </div>
  );
}
